import type {
  AddButton,
  EditorPlugin as EditorPluginType,
  FloatingToolbarButton,
  FormattingToolbarButton,
  LegacyEditorPluginConfig,
  PluginAddButton,
  PluginExports,
  PluginToolbar,
  RicosEditorPlugin,
  RicosEditorPlugins,
  RicosExtension,
  RicosServices,
  TiptapEditorPlugin,
} from 'ricos-types';

import type { PluginServices } from './editorPlugins';
import { PluginTextButton } from './plugin-text-button';
import { RicosPluginAddButton } from './pluginAddButton';
import { RicosPluginToolbar } from './pluginToolbar';

export class EditorPlugin implements RicosEditorPlugin {
  private readonly plugin: EditorPluginType;

  private addButtons?: PluginAddButton[];

  private textButtons?: PluginTextButton[];

  private toolbars?: PluginToolbar[];

  private floatingToolbarButton?: FloatingToolbarButton;

  private exports?: Partial<PluginExports>;

  services: PluginServices;

  static of(
    plugin: EditorPluginType,
    services: PluginServices & { plugins?: RicosEditorPlugins }
  ): EditorPlugin {
    return new EditorPlugin(plugin, services);
  }

  private constructor(plugin: EditorPluginType, services: PluginServices) {
    this.plugin = plugin;
    this.services = services;
  }

  private initAddButtons() {
    if (this.plugin.getAddButtons) {
      this.addButtons = this.plugin
        .getAddButtons(this.plugin.config, this.services as RicosServices)
        .map((button: AddButton) => {
          const addButton = RicosPluginAddButton.of(button, this.services);
          addButton.register();

          return addButton;
        });
    }
  }

  private initTextButtons() {
    if (this.plugin.textButtons) {
      this.textButtons = this.plugin.textButtons.map(b => {
        const pluginTextButton = PluginTextButton.of(
          b,
          this.services,
          this.services.context.platform
        );
        pluginTextButton.register();

        return pluginTextButton;
      });
    }
  }

  private initPluginToolbar() {
    const toolbar = this.plugin.toolbar;
    if (!toolbar) {
      return;
    }

    this.toolbars = toolbar.names.map(name => {
      const ricosPluginToolbar = RicosPluginToolbar.of(
        toolbar,
        name,
        this.services as RicosServices,
        this.plugin.config
      );
      ricosPluginToolbar.register();

      return ricosPluginToolbar;
    });
  }

  private initModals() {
    this.plugin?.modals?.forEach(modal => {
      this.services.modals.register(modal);
    });
  }

  private initFloatingToolbarButton() {
    if (this.plugin.floatingToolbarButton) {
      this.floatingToolbarButton = this.plugin.floatingToolbarButton;
    }
  }

  private initExports() {
    if (this.plugin.exports) {
      this.exports = this.plugin.exports;
    }
  }

  register() {
    this.plugin.shortcuts?.map(shortcut => this.services.shortcuts.register(shortcut));
    this.initAddButtons();
    this.initTextButtons();
    this.initPluginToolbar();
    this.initModals();
    this.initFloatingToolbarButton();
    this.initExports();
  }

  unregister() {
    this.toolbars?.forEach(toolbar => toolbar.unregister());
    this.textButtons?.forEach(b => b.unregister());
    this.addButtons?.forEach(b => b.unregister());
    this.plugin.shortcuts?.forEach(shortcut => this.services.shortcuts.unregister(shortcut));
    this.plugin.modals?.forEach(modal => this.services.modals.unregister(modal.id));
    this.floatingToolbarButton = undefined;
    this.exports = undefined;
  }

  getType(): string {
    return this.plugin.type;
  }

  getTiptapExtensions() {
    return (this.plugin as TiptapEditorPlugin).tiptapExtensions?.map(
      (extension): RicosExtension => ({
        ...extension,
        settings: this.getConfig(),
      })
    );
  }

  getConfig(): LegacyEditorPluginConfig {
    return this.plugin.config;
  }

  getTextButtons(): FormattingToolbarButton[] {
    return this.textButtons || [];
  }

  getAddButtons() {
    return this.addButtons || [];
  }

  getToolbars() {
    return this.toolbars || [];
  }

  getFloatingToolbarButton() {
    return this.floatingToolbarButton || null;
  }

  getExports() {
    return this.exports || {};
  }

  equals(plugin: RicosEditorPlugin) {
    return this.plugin.type === plugin.getType();
  }
}
